import { CodeReviewGroup } from '../../services/codeReviewService';

// Branche ouverte par défaut quand le dépôt l'a (exercice à deux du cycle préparatoire)
export const DEFAULT_BRANCH = 'prepa-commun';

export interface LineRange {
  start: number;
  end: number;
}

// Langages fournis par prism-react-renderer ; une extension absente s'affiche en texte brut
const LANGUAGE_BY_EXTENSION: Record<string, string> = {
  py: 'python',
  html: 'markup', htm: 'markup', jinja: 'markup', j2: 'markup', xml: 'markup', svg: 'markup',
  css: 'css',
  js: 'javascript', mjs: 'javascript', jsx: 'jsx', ts: 'typescript', tsx: 'tsx',
  json: 'json',
  md: 'markdown',
  sql: 'sql',
  yml: 'yaml', yaml: 'yaml',
};

export function languageForPath(path: string): string {
  const name = path.split('/').pop() || '';
  const extension = name.includes('.') ? (name.split('.').pop() || '').toLowerCase() : '';
  return LANGUAGE_BY_EXTENSION[extension] || 'text';
}

// Langage du bloc de code dans la description d'une issue (coloration de GitLab)
function fenceLanguage(path: string): string {
  const language = languageForPath(path);
  if (language === 'markup') return 'html';
  return language === 'text' ? '' : language;
}

// Ordre de l'explorateur de VS Code : à chaque niveau, les dossiers avant les fichiers, puis alphabétique
export function compareTreePaths(a: string, b: string): number {
  const partsA = a.split('/');
  const partsB = b.split('/');
  for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
    if (partsA[i] === partsB[i]) continue;
    const aIsDir = i < partsA.length - 1;
    const bIsDir = i < partsB.length - 1;
    if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;
    return partsA[i].localeCompare(partsB[i], 'fr', { sensitivity: 'base' });
  }
  return partsA.length - partsB.length;
}

export interface TreeRow {
  kind: 'dir' | 'file';
  path: string;
  name: string;
  depth: number;
}

// Lignes de la liste des fichiers : un en-tête par dossier puis ses fichiers, indentés selon la profondeur
export function treeRows(files: string[]): TreeRow[] {
  const rows: TreeRow[] = [];
  let previousDirs: string[] = [];
  for (const path of [...files].sort(compareTreePaths)) {
    const parts = path.split('/');
    const dirs = parts.slice(0, -1);
    let common = 0;
    while (common < dirs.length && common < previousDirs.length && dirs[common] === previousDirs[common]) common++;
    for (let depth = common; depth < dirs.length; depth++) {
      rows.push({ kind: 'dir', path: dirs.slice(0, depth + 1).join('/'), name: dirs[depth], depth });
    }
    rows.push({ kind: 'file', path, name: parts[parts.length - 1], depth: dirs.length });
    previousDirs = dirs;
  }
  return rows;
}

// Fichier ouvert quand aucun n'est choisi : le premier .py de src/ (src/panier.py sur prepa-commun), sinon le README
export function defaultFile(files: string[]): string | null {
  const sorted = [...files].sort(compareTreePaths);
  return sorted.find(p => p.startsWith('src/') && p.endsWith('.py') && !p.endsWith('__init__.py'))
    || sorted.find(p => /^readme(\.md)?$/i.test(p))
    || sorted[0]
    || null;
}

function rangeLabel(path: string, range: LineRange): string {
  return range.start === range.end ? `${path}:${range.start}` : `${path}:${range.start}-${range.end}`;
}

const encodePath = (path: string) => path.split('/').map(encodeURIComponent).join('/');

// Lien permanent : par le SHA du commit et non par la branche, il reste juste après de nouveaux commits
export function blobUrl(webUrl: string, sha: string, path: string, range: LineRange | null = null): string {
  const anchor = !range ? '' : range.start === range.end ? `#L${range.start}` : `#L${range.start}-${range.end}`;
  return `${webUrl}/-/blob/${sha}/${encodePath(path)}${anchor}`;
}

export function commitsUrl(webUrl: string, branch: string): string {
  return `${webUrl}/-/commits/${encodePath(branch)}`;
}

export function graphUrl(webUrl: string, branch: string): string {
  return `${webUrl}/-/network/${encodePath(branch)}`;
}

// Au-delà, l'extrait de code est retiré de la description : l'URL pré-remplie deviendrait trop longue
const MAX_SNIPPET_LINES = 30;
const MAX_ISSUE_URL_LENGTH = 7000;

interface NewIssueParams {
  group: CodeReviewGroup;
  webUrl: string;
  branch: string;
  sha: string;
  path: string;
  range: LineRange;
  lines: string[];
}

// Formulaire « Nouvelle issue » de GitLab pré-rempli : lien permanent vers les lignes, extrait du code et
// mention des membres du groupe (@pseudo), qui reçoivent ainsi le mail de GitLab. La remarque est saisie
// et publiée dans GitLab, sous le compte de l'enseignant·e.
export function newIssueUrl({ group, webUrl, branch, sha, path, range, lines }: NewIssueParams): string {
  const where = rangeLabel(path, range);
  const link = `[${where}](${blobUrl(webUrl, sha, path, range)}) — branche \`${branch}\`, commit ${sha.slice(0, 8)}`;
  const snippetLines = lines.slice(range.start - 1, range.end);
  const fence = snippetLines.some(line => line.includes('```')) ? '````' : '```';
  const snippet = [fence + fenceLanguage(path), ...snippetLines, fence].join('\n');
  const mentions = group.members
    .map(member => member.gitlab_username)
    .filter(Boolean)
    .map(username => `@${username}`)
    .join(' ');

  const build = (withSnippet: boolean) => {
    const description = [link, withSnippet ? snippet : '', 'Remarque : ', mentions].filter(Boolean).join('\n\n');
    const query = new URLSearchParams({ 'issue[title]': `Revue de code : ${where}`, 'issue[description]': description });
    return `${webUrl}/-/issues/new?${query}`;
  };

  const url = build(snippetLines.length <= MAX_SNIPPET_LINES);
  return url.length <= MAX_ISSUE_URL_LENGTH ? url : build(false);
}
